import type { DominantHand } from "@/types/user-types";
import type { EventBus } from "@/types";

const STORAGE_KEY = "ui:handedness";

export type Handedness = DominantHand;

export interface HandednessStateConfig {
  readonly defaultValue?: Handedness;
  readonly storageKey?: string;
  readonly eventBus?: EventBus;
  readonly session?: {
    readonly getSession?: () => unknown;
  } | null;
}

export interface HandednessChangeMeta {
  readonly source?: string;
}

export interface HandednessStateAPI {
  readonly get: () => Handedness;
  readonly set: (handedness: Handedness, meta?: HandednessChangeMeta) => void;
  readonly subscribe: (
    listener: (handedness: Handedness, meta: HandednessChangeMeta) => void
  ) => () => void;
  readonly hydrateFromSession: (session: unknown) => void;
  readonly destroy: () => void;
}

const isHandedness = (value: unknown): value is Handedness =>
  value === "left" || value === "right";

const readDominantHandFromSession = (session: unknown): Handedness | null => {
  if (!session || typeof session !== "object") return null;
  const prefs = (session as Record<string, unknown>).userPrefs;
  if (prefs && typeof prefs === "object") {
    const dominantHand = (prefs as Record<string, unknown>).dominantHand;
    if (isHandedness(dominantHand)) {
      return dominantHand;
    }
  }
  // Some older payloads may expose dominant hand at session.user.dominantHand
  const user = (session as Record<string, unknown>).user;
  if (user && typeof user === "object") {
    const dominantHand = (user as Record<string, unknown>).dominantHand;
    if (isHandedness(dominantHand)) {
      return dominantHand;
    }
  }
  return null;
};

export const createHandednessState = (
  config: HandednessStateConfig = {}
): HandednessStateAPI => {
  const {
    defaultValue = "right",
    storageKey = STORAGE_KEY,
    eventBus,
    session = null,
  } = config;

  const listeners = new Set<
    (handedness: Handedness, meta: HandednessChangeMeta) => void
  >();

  let current: Handedness = (() => {
    const fromSession = session?.getSession
      ? readDominantHandFromSession(session.getSession())
      : null;
    if (fromSession) return fromSession;

    if (typeof window !== "undefined") {
      try {
        const persisted = window.localStorage.getItem(storageKey);
        if (isHandedness(persisted)) {
          return persisted;
        }
      } catch {
        // Ignore storage failures
      }
    }

    return defaultValue;
  })();

  const persist = (handedness: Handedness) => {
    if (typeof window === "undefined") return;
    try {
      window.localStorage.setItem(storageKey, handedness);
    } catch {
      // Persistence failures are non-fatal
    }
  };

  const notify = (handedness: Handedness, meta: HandednessChangeMeta) => {
    for (const listener of listeners) {
      try {
        listener(handedness, meta);
      } catch {
        // Individual listener errors should not break notification chain
      }
    }
  };

  const emitEvent = (handedness: Handedness, meta: HandednessChangeMeta) => {
    if (!eventBus) return;
    try {
      eventBus.emit("ui:handedness:changed", {
        handedness,
        source: meta.source,
      });
    } catch {
      // Event emission is best-effort
    }
  };

  const set = (handedness: Handedness, meta: HandednessChangeMeta = {}) => {
    if (!isHandedness(handedness)) return;
    if (current === handedness) return;
    current = handedness;
    persist(handedness);
    notify(handedness, meta);
    emitEvent(handedness, meta);
  };

  const hydrateFromSession = (sessionPayload: unknown) => {
    const dominantHand = readDominantHandFromSession(sessionPayload);
    if (!dominantHand) return;
    set(dominantHand, { source: "session" });
  };

  const subscribe = (
    listener: (handedness: Handedness, meta: HandednessChangeMeta) => void
  ) => {
    listeners.add(listener);
    listener(current, { source: "init" });
    return () => listeners.delete(listener);
  };

  let unsubscribeSessionUpdated: (() => void) | null = null;
  if (eventBus && typeof eventBus.on === "function") {
    try {
      unsubscribeSessionUpdated = eventBus.on(
        "session:updated",
        (payload: { session?: unknown } | undefined) => {
          if (!payload) return;
          hydrateFromSession(payload.session);
        }
      );
    } catch {
      // Ignore subscription failures
    }
  }

  const destroy = () => {
    listeners.clear();
    if (unsubscribeSessionUpdated) {
      try {
        unsubscribeSessionUpdated();
      } catch {
        // Ignore teardown failures
      }
      unsubscribeSessionUpdated = null;
    }
  };

  // Emit initial event so late subscribers (components) can align with persisted value
  emitEvent(current, { source: "init" });

  return {
    get: () => current,
    set,
    subscribe,
    hydrateFromSession,
    destroy,
  };
};
